
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CookiePreferences } from '@/types/cookie-consent';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Cookie, Shield } from 'lucide-react';

const cookieCategories = [
  {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'Essential cookies that enable basic website functionality and security features.',
    required: true,
  },
  {
    id: 'functional',
    name: 'Functional',
    description: 'Cookies that enhance website functionality and personalization.',
    required: false,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Cookies that help us understand how visitors interact with our website.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Cookies used for marketing and advertising purposes.',
    required: false,
  },
];

export function CookieConsentBanner() {
  const { showBanner, preferences, savePreferences } = useCookieConsent();
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(preferences);

  const handleAcceptAll = () => {
    const allEnabled: CookiePreferences = {
      ...preferences,
      functional: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allEnabled);
  };

  const handleSavePreferences = () => {
    savePreferences(localPreferences);
  };

  const handleToggle = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    setLocalPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showBanner) return null;

  return (
    <Dialog open={showBanner} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5" />
            Cookie Preferences
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            We use cookies to enhance your browsing experience and analyze our traffic. Please choose your preferences below.
          </p>
          
          <div className="space-y-4">
            {cookieCategories.map((category) => (
              <div key={category.id} className="flex items-start justify-between space-x-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-nation-blue" />
                    <h4 className="font-medium">
                      {category.name}
                      {category.required && (
                        <span className="ml-2 text-xs text-gray-500">(Required)</span>
                      )}
                    </h4>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{category.description}</p>
                </div>
                <Switch
                  checked={!!localPreferences[category.id as keyof CookiePreferences]} // Use double negation to convert to boolean
                  onCheckedChange={() => handleToggle(category.id as keyof CookiePreferences)}
                  disabled={category.required}
                />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={handleSavePreferences}>
            Save Preferences
          </Button>
          <Button onClick={handleAcceptAll}>
            Accept All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
